import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { CategoryRow } from '../../../components/categories/CategoryRow';
import type { FilteredCanton, UseWorkRegionTreeReturn } from '../state/useWorkRegionTree';

type WorkRegionTreeProps = Omit<UseWorkRegionTreeReturn, 'visiblePostalCodes' | 'allSelected' | 'toggleAll'> & {
    selectedPostalCodes: ReadonlySet<string>;
    disabled?: boolean;
};

export function WorkRegionTree({
    filteredCantonNodes,
    expandedCantons,
    selectedPostalCodes,
    toggleExpandCanton,
    toggleCantonSelection,
    togglePostalCode,
    disabled,
}: WorkRegionTreeProps): ReactElement {
    const { t } = useTranslation();

    return (
        <Stack
            spacing={0.5}
            component="ul"
            sx={{
                listStyle: 'none',
                p: 0,
                m: 0,
                // A nested scroller traps touch drags, so the list grows and the page scrolls instead.
                overflowY: { xs: 'visible', md: 'auto' },
                height: { xs: 'auto', md: 480 },
            }}
            aria-label={t('SETTINGS.COMPANY.CANTONS.ARIA_LABEL')}>
            {filteredCantonNodes.map((canton) => (
                <li key={canton.id}>
                    <CantonDropdown
                        canton={canton}
                        isExpanded={expandedCantons.has(canton.id)}
                        selectedPostalCodes={selectedPostalCodes}
                        onToggleExpand={() => toggleExpandCanton(canton.id)}
                        onToggleCantonSelection={() => toggleCantonSelection(canton.id)}
                        onTogglePostalCode={togglePostalCode}
                        disabled={disabled}
                    />
                </li>
            ))}
        </Stack>
    );
}

type CantonDropdownProps = {
    canton: FilteredCanton;
    isExpanded: boolean;
    selectedPostalCodes: ReadonlySet<string>;
    onToggleExpand: () => void;
    onToggleCantonSelection: () => void;
    onTogglePostalCode: (code: string) => void;
    disabled?: boolean;
};

function CantonDropdown({
    canton,
    isExpanded,
    selectedPostalCodes,
    onToggleExpand,
    onToggleCantonSelection,
    onTogglePostalCode,
    disabled,
}: CantonDropdownProps): ReactElement {
    const { t } = useTranslation();
    const selectedCount = canton.postalCodes.filter(({ code }) => selectedPostalCodes.has(code)).length;
    const isFullySelected = canton.postalCodes.length > 0 && selectedCount === canton.postalCodes.length;
    const isIndeterminate = selectedCount > 0 && !isFullySelected;
    const label = t(`SETTINGS.COMPANY.CANTONS.${canton.id}`, { defaultValue: canton.label });

    return (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: '4px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1, py: 0.5 }}>
                <CategoryRow
                    label={`${label} (${selectedCount})`}
                    checked={isFullySelected}
                    indeterminate={isIndeterminate}
                    open={isExpanded}
                    onChange={onToggleCantonSelection}
                    onToggleOpen={onToggleExpand}
                    disabled={disabled}
                />
            </Box>

            {isExpanded && (
                <Box>
                    <Divider />
                    <Box sx={{ pl: 7 }}>
                        {canton.postalCodes.map(({ code, name }) => (
                            <FormControlLabel
                                key={code}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={selectedPostalCodes.has(code)}
                                        onChange={() => onTogglePostalCode(code)}
                                        disabled={disabled}
                                    />
                                }
                                label={name ? `${code} | ${name}` : code}
                                sx={{ display: 'flex', mr: 0, my: 0 }}
                            />
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
}
